import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from 'react'
import type { AppState, ServerMessage } from '@shared/types'
import { ApiFailure, GET, flushQueue, queueSize } from './api'

type Status = 'loading' | 'ready' | 'unauthenticated' | 'needs-profile' | 'error'

interface StoreValue {
  status: Status
  state: AppState | null
  error: string | null
  /** refetch /api/state; `quiet` skips the loading flash */
  refresh: (quiet?: boolean) => Promise<void>
  /**
   * Fold a known change into the shared state without a round trip.
   *
   * A screen that owns its own copy of some slice (Picks fetches its week
   * separately) would otherwise leave every other screen stale until the next
   * refresh: make sixteen picks, go Home, and the counter still reads 0 of 16
   * until a hard reload. Refetching all of /api/state on every tap is the
   * wrong trade for a counter, so the screen reports the delta instead.
   */
  patch: (fn: (prev: AppState) => AppState) => void
  online: boolean
  pending: number
  live: boolean
  toast: (message: string, action?: { label: string; run: () => void }) => void
  toasts: { id: number; message: string; action?: { label: string; run: () => void } }[]
  dismissToast: (id: number) => void
}

const Ctx = createContext<StoreValue | null>(null)

export function useStore(): StoreValue {
  const v = useContext(Ctx)
  if (!v) throw new Error('useStore outside StoreProvider')
  return v
}

/** Convenience: the resolved state, throwing if not ready. Screens are only
 *  mounted once status === 'ready', so this is safe inside them. */
export function useAppState(): AppState {
  const { state } = useStore()
  if (!state) throw new Error('app state not loaded')
  return state
}

export function StoreProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading')
  const [state, setState] = useState<AppState | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine)
  const [pending, setPending] = useState(0)
  const [live, setLive] = useState(false)
  const [toasts, setToasts] = useState<StoreValue['toasts']>([])
  const toastId = useRef(0)

  const toast = useCallback((message: string, action?: { label: string; run: () => void }) => {
    const id = ++toastId.current
    setToasts((t) => [...t, { id, message, action }])
  }, [])
  const dismissToast = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), [])

  const patch = useCallback((fn: (prev: AppState) => AppState) => {
    setState((prev) => (prev ? fn(prev) : prev))
  }, [])

  const refresh = useCallback(async (quiet = false) => {
    if (!quiet) setStatus((s) => (s === 'ready' ? s : 'loading'))
    try {
      const next = await GET<AppState>('/api/state')
      setState(next)
      setError(null)
      setStatus(next.user.nameState === 'pending' ? 'needs-profile' : 'ready')
    } catch (e) {
      if (e instanceof ApiFailure && e.code === 'unauthenticated') {
        setStatus('unauthenticated')
        return
      }
      if (e instanceof ApiFailure && e.code === 'name_pending') {
        setStatus('needs-profile')
        return
      }
      // Offline or a server blip: keep whatever we last rendered (D103).
      if (state) {
        setStatus('ready')
        setError(e instanceof Error ? e.message : 'Connection problem')
      } else {
        setStatus('error')
        setError(e instanceof Error ? e.message : 'Could not load')
      }
    }
  }, [state])

  useEffect(() => { void refresh() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [])

  // Online / offline + queue replay
  useEffect(() => {
    const sync = () => setPending(queueSize())
    sync()
    const goOnline = async () => {
      setOnline(true)
      const { sent, failed } = await flushQueue()
      sync()
      if (sent) { toast(`${sent} change${sent === 1 ? '' : 's'} saved`); void refresh(true) }
      if (failed) toast(`${failed} change${failed === 1 ? '' : 's'} couldn’t be saved`)
    }
    const goOffline = () => { setOnline(false); sync() }
    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    const t = setInterval(sync, 4000)
    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
      clearInterval(t)
    }
  }, [refresh, toast])

  // Refresh when the tab comes back to the foreground.
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') void refresh(true) }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [refresh])

  // Realtime. Reconnects with backoff + jitter; falls back to polling after two
  // failed upgrades (D107).
  useEffect(() => {
    if (status !== 'ready') return
    let ws: WebSocket | null = null
    let closed = false
    let attempt = 0
    let pollTimer: ReturnType<typeof setInterval> | null = null
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    let debounce: ReturnType<typeof setTimeout> | null = null

    const nudge = () => {
      if (debounce) clearTimeout(debounce)
      debounce = setTimeout(() => { void refresh(true) }, 400)
    }

    const startPolling = () => {
      if (pollTimer) return
      setLive(false)
      pollTimer = setInterval(() => { void refresh(true) }, 30_000)
    }

    const connect = () => {
      if (closed) return
      try {
        const proto = location.protocol === 'https:' ? 'wss' : 'ws'
        ws = new WebSocket(`${proto}://${location.host}/ws`)
      } catch {
        startPolling()
        return
      }

      ws.onopen = () => { attempt = 0; setLive(true); if (pollTimer) { clearInterval(pollTimer); pollTimer = null } }

      ws.onmessage = (ev) => {
        let msg: ServerMessage
        try { msg = JSON.parse(ev.data as string) as ServerMessage } catch { return }
        switch (msg.t) {
          case 'rollover':
            // Local midnight: reconnect to the new date's Room (D120).
            ws?.close()
            void refresh(true)
            break
          case 'score':
          case 'scoring':
          case 'challenge':
          case 'food':
          case 'fund':
          case 'attend':
          case 'shout':
          case 'stale':
            nudge()
            break
          default:
            break
        }
      }

      ws.onclose = () => {
        setLive(false)
        if (closed) return
        attempt++
        if (attempt >= 2) startPolling()
        const backoff = Math.min(30_000, 1000 * 2 ** Math.min(attempt, 5))
        retryTimer = setTimeout(connect, backoff + Math.random() * 800)
      }

      ws.onerror = () => ws?.close()
    }

    connect()
    return () => {
      closed = true
      ws?.close()
      if (pollTimer) clearInterval(pollTimer)
      if (retryTimer) clearTimeout(retryTimer)
      if (debounce) clearTimeout(debounce)
    }
  }, [status, refresh])

  const value = useMemo<StoreValue>(
    () => ({ status, state, error, refresh, patch, online, pending, live, toast, toasts, dismissToast }),
    [status, state, error, refresh, patch, online, pending, live, toast, toasts, dismissToast],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
